(ns nl.mediquest.ui.core.header
  (:require
   [clojure.spec.alpha :as s]
   [clojure.string :as string]
   [integrant.core :as ig]
   [nl.mediquest.ui.core.search]
   [nl.mediquest.ui.util :refer [optional-ref matches-key?]]
   [reagent.core :as r]))

(s/def :menu.item/url string?)
(s/def :menu.item/fn fn?)
(s/def :menu.item/title string?)
(s/def :menu.item/separator boolean?)
(s/def :menu.item/children
  (s/coll-of :menu/item))

(s/def :menu/link
  (s/keys :req [:menu.item/url
                :menu.item/title]))

(s/def :menu/on-click
  (s/keys :req [:menu.item/on-click
                :menu.item/title]))

(s/def :menu/parent
  (s/keys :req [:menu.item/children
                :menu.item/title]))

(s/def :menu/empty map?)

(s/def :menu/separator
  (s/keys :req [:menu.item/separator]))

(s/def :menu/item
  (s/or :item :menu/link
        :parent :menu/parent
        :separator :menu/separator
        :empty :menu/empty))

(s/def :header/title string?)
(s/def :header/logo string?)
(s/def :header/portaal-url string?)
(s/def :header/user-component fn?)
(s/def :header/search fn?) ; (initialized :component.ui/search)
(s/def :header/menu
  (s/coll-of :menu/item))

(declare menu-items)

(defn clicked? [event id]
  (-> event .-target (.closest id)))

(defn off-click-event-handler [menu-hidden? event]
  (cond
    (clicked? event ".menu-link") (reset! menu-hidden? true)
    (clicked? event ".menu") (swap! menu-hidden? not)
    (not (clicked? event ".menu-wrapper")) (reset! menu-hidden? true)))

(defn menu-separator []
  [:div.border-t.border-gray-200.mx-3])

(defn menu-parent
  [exclusions {:menu.item/keys [title children]}]
  (let [hidden? (r/atom true)]
    (fn []
      [:div
       [:div.hover:bg-gray-100.hover:text-blue-400.flex.items-center.justify-between.cursor-pointer
        {:on-click #(swap! hidden? not)}
        [:a.block.py-2.px-3.select-none title]
        [:i.fas.pr-3
         {:class [(if @hidden? :fa-chevron-down :fa-chevron-up)]}]]
       [:div.ml-2.text-sm {:class [(when @hidden? :hidden)]}
        [menu-items exclusions children]]])))

(defn menu-link-opts [{:menu.item/keys [url target]}]
  (if target
    {:href url
     :target target}
    {:href url}))

(defn menu-link [{:menu.item/keys [title on-click] :as menu-item}]
  [:div.hover:bg-gray-100.hover:text-blue-400.hover:cursor-pointer
   (cond-> {}
     on-click (assoc :on-click on-click))
   [:a.block.py-2.px-3.menu-link (menu-link-opts menu-item) title]])

(defn menu-item
  [exclusions {:menu.item/keys [id visible?]
               :or {visible? true}
               :as item}]
  (when (if (fn? visible?)
          (visible?)
          visible?)
    (when-not (matches-key? exclusions id)
      (cond
        (s/valid? :menu/separator item) [menu-separator]
        (s/valid? :menu/parent item) [menu-parent exclusions item]
        (s/valid? :menu/link item) [menu-link item]
        (s/valid? :menu/on-click item) [menu-link item]
        (s/valid? :menu/empty item) nil))))

(defn menu-items [exclusions menu]
  [:<>
   (doall
    (map-indexed (fn [id item] ^{:key id} [menu-item exclusions item]) menu))])

(defn- menu-vsvs [vsv-names]
  (if (> (count vsv-names) 1)
    [:div.py-2.px-3 (str "VSV's: " (string/join ", " vsv-names))]
    [:div.py-2.px-3 (str "VSV: " (first vsv-names))]))

(defn- render-menu
  [menu-hidden? menu exclusions user-info]
  [:div.container.mx-auto.menu-wrapper.text-black.print:hidden
   [:div.float-right.shadow-lg.bg-white.w-full
    {:class [(when (and menu-hidden? @menu-hidden?) :hidden)]
     :style {:max-width "370px"}}
    (when user-info
      [:<>
       [:div.py-2.px-3 (or (:name user-info) (:email user-info))]
       [menu-separator]
       [:div.py-2.px-3 (:organisatie-naam user-info)]
       (when-let [vsv-names (:vsv_naam user-info)]
         [menu-vsvs vsv-names])
       [menu-separator]])
    [menu-items exclusions menu]]])

(defn header-component
  [{:header/keys [title logo portaal-url user-info user-component menu color sticky? search]
    :header.menu.item/keys [exclusions]}
   menu-hidden?]
  [:div.h-16.border-b.border-gray-200.bg-white.z-30
   {:class (when-not (false? sticky?) ["sticky" "top-0" "w-full" "self-start"])}
   [:div.container.mx-auto.h-16.leading-none
    [:div.h-full.items-center.flex.justify-between.content-center
     [:div.flex.pl-4
      (if portaal-url
        [:a {:href portaal-url}
         [:img.h-8 {:src logo}]]
        [:img.h-8 {:src logo}])
      [:div.border-l.border-gray-200.md:mx-10.md:block.hidden]
      [:div.text-2xl.text-primary.leading-tight.md:block.hidden.font-semibold
       {:style {:color color}}
       title]]
     [:div.flex-wrap.flex.space-x-4.pr-4.items-center.print:hidden
      (when search
        [search])
      (cond
        user-component [user-component]
        user-info [:div.flex.items-center.hidden.md:block
                   [:i.fas.fa-user-circle.text-3xl.text-primary]])
      [:div.h-full.flex-wrap.flex.content-center.menu.hover:cursor-pointer
       {:role :button
        :class [(when-not menu :hidden)]}
       [:i.fal.fa-bars.text-xl.text-gray-600.hover:text-blue-400.menu]]]]]
   [render-menu menu-hidden? menu exclusions user-info]])

(defn view [header-config]
  (r/with-let [menu-hidden? (r/atom true)
               handler (partial off-click-event-handler menu-hidden?)
               _ (js/document.addEventListener "click" handler)]
    [header-component header-config menu-hidden?]
    (finally
      (js/document.removeEventListener "click" handler))))

(defmethod ig/assert-key :component.ui/header [_k v]
  (assert (s/valid? (s/keys :req [:header/title
                                  :header/logo
                                  :header/user-info]
                            :opt [:header/portaal-url
                                  :header/user-component
                                  :header/search
                                  :header/menu])
                    v)))

(defmethod ig/expand-key :component.ui/header [k opts]
  {k
   (-> opts
       (assoc :header/user-info (ig/ref :user/info)
              :header.menu.item/exclusions
              (optional-ref :header.menu.item/exclusions [])))})

(defmethod ig/init-key :component.ui/header
  [_ header-config]
  (fn []
    [view header-config]))
